import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Mail, RefreshCw, CheckCircle2, AlertTriangle, Clock, ShieldAlert, Loader2 } from "lucide-react";

/**
 * Gmail Status card — Gmail Production Reliability v1 §5 + §9.
 *
 * One compact operational card on the Inbox page. Reads the canonical
 * `/api/inbox/status` endpoint (single source of truth) and renders
 * exactly the fields the executive needs:
 *   - connected account
 *   - current state (one of six explicit states)
 *   - last successful sync
 *   - last attempted sync
 *   - messages / duplicates / errors from last run
 *   - retry state with next_retry_at when relevant
 *   - Reconnect Gmail button when required
 * Deliberately NOT a raw JSON diagnostic — that surface still lives
 * behind the "Queue diagnostic" toggle for deep debugging.
 */

const STATE_META = {
  connected: {
    label: "Synced",
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/25",
    Icon: CheckCircle2,
  },
  syncing: {
    label: "Syncing",
    color: "text-blue-300 bg-blue-500/10 border-blue-500/25",
    Icon: Loader2,
    spin: true,
  },
  rate_limited: {
    label: "Rate-limited",
    color: "text-amber-300 bg-amber-500/10 border-amber-500/25",
    Icon: AlertTriangle,
  },
  retry_scheduled: {
    label: "Retry scheduled",
    color: "text-amber-300 bg-amber-500/10 border-amber-500/25",
    Icon: Clock,
  },
  reauthorization_required: {
    label: "Reconnect required",
    color: "text-red-300 bg-red-500/10 border-red-500/25",
    Icon: ShieldAlert,
  },
  disconnected: {
    label: "Disconnected",
    color: "text-white/50 bg-white/[0.03] border-white/10",
    Icon: Mail,
  },
  not_connected: {
    label: "Not connected",
    color: "text-white/50 bg-white/[0.03] border-white/10",
    Icon: Mail,
  },
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
}

export default function GmailStatusCard({ onReconnect, onSync, busy }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/inbox/status");
      setStatus(r.data);
    } catch (e) {
      // Endpoint unreachable — the InboxPage's own auth handler will
      // redirect on 401; here we quietly show a neutral card.
      setStatus(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Poll every 20s so the state chip updates while a sync is running.
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !status) {
    return (
      <div className="mb-4 p-4 rounded-xl border border-white/10 bg-white/[0.02] text-sm text-white/50" data-testid="gmail-status-card-loading">
        Loading Gmail status…
      </div>
    );
  }

  const meta = STATE_META[status.state] || STATE_META.connected;
  const { Icon, spin } = meta;

  return (
    <div className="mb-4 p-4 rounded-xl border border-white/10 bg-[#111621]" data-testid="gmail-status-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] uppercase tracking-wider ${meta.color}`}
              data-testid="gmail-status-state"
            >
              <Icon className={`w-3.5 h-3.5 ${spin ? "animate-spin" : ""}`} />
              {meta.label}
            </span>
            {status.connected_account && (
              <span className="text-sm font-medium text-white" data-testid="gmail-status-account">
                {status.connected_account}
              </span>
            )}
            {status.queue_size > 0 && (
              <span className="text-[10px] uppercase tracking-wider text-amber-200/70" data-testid="gmail-status-queue-size">
                {status.queue_size} queued
              </span>
            )}
          </div>

          {status.last_error && (
            <p className="mt-2 text-xs text-white/70 max-w-2xl" data-testid="gmail-status-last-error">
              {status.last_error}
            </p>
          )}

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-[11px] text-white/60">
            <div>
              <div className="text-white/40 uppercase tracking-wider text-[10px]">Last success</div>
              <div data-testid="gmail-status-last-success">{fmtTs(status.last_successful_sync_at)}</div>
            </div>
            <div>
              <div className="text-white/40 uppercase tracking-wider text-[10px]">Last attempted</div>
              <div data-testid="gmail-status-last-attempted">{fmtTs(status.last_attempted_sync_at)}</div>
            </div>
            {status.last_run && (
              <>
                <div>
                  <div className="text-white/40 uppercase tracking-wider text-[10px]">Msgs last run</div>
                  <div data-testid="gmail-status-msgs-last-run">
                    {status.last_run.messages}
                    {status.last_run.duplicates > 0 && (
                      <span className="text-white/40"> · {status.last_run.duplicates} dupes</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-wider text-[10px]">Mode</div>
                  <div>{status.last_run.mode || "—"}{status.last_run.errors > 0 ? ` · ${status.last_run.errors} err` : ""}</div>
                </div>
              </>
            )}
          </div>

          {status.retry_state && (
            <div className="mt-3 text-[11px] text-amber-200/80" data-testid="gmail-status-retry-state">
              <Clock className="w-3 h-3 inline mr-1" />
              {status.retry_state.quota_kind === "daily_limited" ? "Daily quota" : "Rate limit"} —
              {" "}next retry at {fmtTs(status.retry_state.next_retry_at)}
              {status.retry_state.queue_deferred > 0 && (
                <> · {status.retry_state.queue_deferred} deferred / {status.retry_state.queue_eligible} eligible</>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 items-end">
          {status.reconnect_required ? (
            <Button onClick={onReconnect} className="rounded-full bg-white text-black hover:bg-white/90" size="sm"
                    data-testid="gmail-status-reconnect-btn">
              <Mail className="w-3.5 h-3.5 mr-2" /> Reconnect Gmail
            </Button>
          ) : (
            <Button onClick={async () => {
                     // Await the sync POST so we reload AFTER the
                     // backend has finalized status — no 800 ms fudge.
                     try { if (onSync) await onSync(); }
                     finally { await load(); }
                   }}
                    disabled={busy || status.state === "syncing"}
                    variant="outline" size="sm"
                    className="rounded-full"
                    data-testid="gmail-status-sync-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Sync now
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
